import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Box,
  useMediaQuery,
  CircularProgress,
  Avatar,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  getReceivedInvitations,
  getSentInvitations,
  getPartnershipStats,
  getPartnerships,
  acceptInvitation,
  declineInvitation,
  cancelInvitation,
  resendInvitation,
} from "../customerPartnershipsSlice";
import {
  CustomerPartnershipsState,
  PartnerRelationship,
  RelationshipTierCode,
  PartnerInvitation,
  InvitationStatusCode,
} from "../models/models";
import { CustomerSettingsState } from "../../settings/models/models";
import { getCustomer } from "../../settings/customerSettingsSlice";
import { tierStyleMap, invitationStatusStyleMap } from "../utils/tierMaps";
import { dt, paginationWrapperSx, StatusBadgeColorConfig } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import RedesignStatusBadge from "../../../shared/components/redesign/RedesignStatusBadge";
import RedesignFilterItem from "../../../shared/components/redesign/RedesignFilterItem";
import InvitePartnerButton from "./InvitePartnerButton";
import AcceptInvitationDialog from "./AcceptInvitationDialog";
import LightTablePagination from "../../../shared/components/tables/LightTablePagination";
import useDataRefresh from "../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../shared/hooks/useVisibilityRefresh";
import { useChangeHighlight } from "../../../shared/hooks/useChangeHighlight";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import { useToastStack, ToastStack } from "../../../shared/components/ActionToast";

// ─── View type ──────────────────────────────────────────────
type ActiveView = "partnerships" | "sentInvitations" | "receivedInvitations";

// ─── Sort direction for local sorting ───────────────────────
type SortDir = "asc" | "desc";

// ─── Tier icon map ──────────────────────────────────────────
const tierIconMap: Record<RelationshipTierCode, React.ReactNode> = {
  [RelationshipTierCode.Connected]: <HandshakeOutlinedIcon sx={{ fontSize: "14px" }} />,
  [RelationshipTierCode.Preferred]: <StarOutlinedIcon sx={{ fontSize: "14px" }} />,
  [RelationshipTierCode.Contracted]: <DescriptionOutlinedIcon sx={{ fontSize: "14px" }} />,
};

// ─── Status badge colors for RedesignStatusBadge ────────────
const tierBadgeColors: Record<RelationshipTierCode, StatusBadgeColorConfig> = {
  [RelationshipTierCode.Connected]: { bg: "#EBF5FB", text: dt.blue, border: "#AED6F1" },
  [RelationshipTierCode.Preferred]: { bg: "#FEF9E7", text: "#F59E0B", border: "#F9E79F" },
  [RelationshipTierCode.Contracted]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
};

const invitationBadgeColors: Record<InvitationStatusCode, StatusBadgeColorConfig> = {
  [InvitationStatusCode.Pending]: { bg: "#EBF5FB", text: dt.blueHover, border: "#AED6F1" },
  [InvitationStatusCode.Accepted]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  [InvitationStatusCode.Declined]: { bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1" },
  [InvitationStatusCode.Expired]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
  [InvitationStatusCode.Cancelled]: { bg: "#F4F6F9", text: "#9CA3AF", border: "#D1D5DB" },
  [InvitationStatusCode.Superseded]: { bg: "#F4F6F9", text: "#9CA3AF", border: "#D1D5DB" },
};

// ─── Shared table styles (elevated, matching MyServiceRequests) ──
const tableWrapperSx = {
  borderRadius: dt.radiusLg,
  overflow: "hidden",
  border: `1px solid ${dt.cardBorder}`,
  boxShadow: dt.cardShadow,
  backgroundColor: dt.cardBg,
};

const tableSx = {
  "& thead th": {
    backgroundColor: dt.headerBg,
    borderBottom: `1px solid ${dt.cardBorder}`,
    fontSize: "11px !important",
    fontWeight: "700 !important",
    textTransform: "uppercase",
    letterSpacing: "1.2px",
    color: "#64748B !important",
    fontFamily: `${dt.font} !important`,
    padding: "14px 18px",
    whiteSpace: "nowrap",
  },
  "& thead th:first-of-type": { paddingLeft: "24px" },
  "& thead th:nth-of-type(1)": { color: `${dt.blue} !important` },
  "& thead .MuiTableSortLabel-root": {
    fontSize: "11px !important",
    fontWeight: "700 !important",
    textTransform: "uppercase",
    letterSpacing: "1.2px",
    color: "#64748B !important",
    fontFamily: `${dt.font} !important`,
  },
  "& thead .MuiTableSortLabel-root.Mui-active": { color: `${dt.blue} !important` },
  "& thead .MuiTableSortLabel-icon": { color: "#9CA3AF !important" },
  "& tbody td:first-of-type": { paddingLeft: "24px" },
  "& tbody tr": {
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:nth-of-type(even)": {
      backgroundColor: "#FAFBFD",
    },
    "&:hover": {
      backgroundColor: dt.rowHover,
      boxShadow: `inset 3px 0 0 0 ${dt.blue}`,
    },
  },
  "& tbody td": {
    padding: "14px 18px",
    borderBottom: `1px solid ${dt.rowBorder}`,
    fontSize: "14px",
    color: dt.textPrimary,
  },
  "& tbody tr:last-child td": { borderBottom: "none" },
};

// =================================================================
// PARTNERSHIPS PAGE
// =================================================================

const PartnershipsPage: React.FC = () => {
  const { translate } = useTranslation(["partnerships", "common"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const isCompact = useMediaQuery("(max-width:1300px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  useRedesignSetup();

  // ─── Inject rowHighlight keyframe animation ──────────────
  const highlightStyleInjected = useRef(false);
  useEffect(() => {
    if (highlightStyleInjected.current) return;
    highlightStyleInjected.current = true;
    const style = document.createElement("style");
    style.textContent = `
      @keyframes rowHighlight {
        0% { background-color: rgba(25, 118, 210, 0.12); }
        100% { background-color: transparent; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const {
    stats,
    partnerships,
    invitationsReceived,
    invitationsSent,
    loading,
    actionLoading,
    receivedQuery,
    sentQuery,
    partnershipsQuery,
    error,
  } = useAppSelector<CustomerPartnershipsState>((state: any) => state.customerPartnerships);

  const { customer: ownCustomer } = useAppSelector<CustomerSettingsState>(
    (state: any) => state.customerSettings
  );

  const [activeView, setActiveView] = useState<ActiveView>("partnerships");
  const [tierFilter, setTierFilter] = useState<RelationshipTierCode | null>(null);
  const [searchText, setSearchText] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const { addToast, dismissToast, toasts } = useToastStack();

  // Confirmation dialogs state
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<PartnerInvitation | null>(null);

  // ─── Local sort state for Sent Invitations ──────────────
  const [sentSortField, setSentSortField] = useState<"status" | "date">("date");
  const [sentSortDir, setSentSortDir] = useState<SortDir>("desc");

  // ─── Data Loading ──────────────────────────────────────────

  useEffect(() => {
    dispatch(getReceivedInvitations(receivedQuery));
    dispatch(getSentInvitations(sentQuery));
    dispatch(getPartnerships(partnershipsQuery));
    dispatch(getPartnershipStats());
    dispatch(getCustomer());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAll = useCallback(() => {
    dispatch(getReceivedInvitations(receivedQuery));
    dispatch(getSentInvitations(sentQuery));
    dispatch(getPartnerships(partnershipsQuery));
    dispatch(getPartnershipStats());
  }, [dispatch, receivedQuery, sentQuery, partnershipsQuery]);

  // ─── Real-Time Updates ─────────────────────────────────────
  useDataRefresh("partnerships", refreshAll);
  useVisibilityRefresh(refreshAll);

  // ─── Stale-while-revalidate: only show spinner on first load ─
  const hasPartnershipsData = partnerships !== undefined;
  const hasSentData = invitationsSent !== undefined;
  const hasReceivedData = invitationsReceived !== undefined;

  // ─── Change Highlight Tracking ──────────────────────────────
  const getPartnershipKey = useCallback((p: PartnerRelationship) => p.id, []);
  const getPartnershipHash = useCallback(
    (p: PartnerRelationship) => `${p.effectiveTier}-${p.tierSetByA}-${p.tierSetByB}-${p.isActive}`,
    []
  );
  const getInvitationKey = useCallback((i: PartnerInvitation) => i.id, []);
  const getInvitationHash = useCallback(
    (i: PartnerInvitation) => `${i.statusCode}`,
    []
  );

  const partnershipItems = useMemo(() => partnerships?.items || [], [partnerships]);
  const sentItems = useMemo(() => invitationsSent?.items || [], [invitationsSent]);
  const receivedItems = useMemo(() => invitationsReceived?.items || [], [invitationsReceived]);

  const changedPartnershipKeys = useChangeHighlight(partnershipItems, getPartnershipKey, getPartnershipHash);
  const changedSentKeys = useChangeHighlight(sentItems, getInvitationKey, getInvitationHash);
  const changedReceivedKeys = useChangeHighlight(receivedItems, getInvitationKey, getInvitationHash);

  const highlightRowSx = { animation: "rowHighlight 1.5s ease-out" };

  const handleInviteSuccess = (email?: string) => {
    addToast("success", translate("partnerships:invite.successMessage", { email: email || "" }));
    refreshAll();
  };

  const handleViewChange = (view: ActiveView) => {
    setActiveView(view);
    setSearchText("");
    setTierFilter(null);
  };

  // ─── Actions ───────────────────────────────────────────────

  const handleAcceptClick = (invitation: PartnerInvitation) => {
    setSelectedInvitation(invitation);
    setAcceptDialogOpen(true);
  };

  const handleAcceptConfirm = async () => {
    if (!selectedInvitation) return;
    try {
      await dispatch(acceptInvitation(selectedInvitation.id)).unwrap();
      setAcceptDialogOpen(false);
      const companyName = selectedInvitation.inviterCustomerName || selectedInvitation.inviteeEmail;
      addToast("success", translate("partnerships:accept.successMessage", { companyName }));
      setSelectedInvitation(null);
      refreshAll();
    } catch (err: any) {
      addToast("error", err?.message || translate("partnerships:accept.errorMessage"));
    }
  };

  const handleDeclineClick = (invitation: PartnerInvitation) => {
    setSelectedInvitation(invitation);
    const companyName = invitation.inviterCustomerName || invitation.inviteeEmail || "";
    confirm({
      title: translate("partnerships:decline.confirmTitle"),
      text: translate("partnerships:decline.confirmMessage", { companyName }),
      confirmButton: translate("partnerships:decline.confirm"),
      closeButton: translate("partnerships:decline.cancel"),
      severity: "error",
      onConfirm: async () => {
        try {
          await dispatch(declineInvitation(invitation.id)).unwrap();
          addToast("success", translate("partnerships:decline.successMessage"));
          setSelectedInvitation(null);
          refreshAll();
        } catch (err: any) {
          addToast("error", err?.message || translate("partnerships:decline.errorMessage"));
        }
      },
    });
  };

  const handleCancelClick = (invitation: PartnerInvitation) => {
    setSelectedInvitation(invitation);
    confirm({
      title: translate("partnerships:cancel.confirmTitle"),
      text: translate("partnerships:cancel.confirmMessage", { email: invitation.inviteeEmail || "" }),
      confirmButton: translate("partnerships:cancel.confirm"),
      closeButton: translate("partnerships:cancel.cancel"),
      severity: "neutral",
      onConfirm: async () => {
        try {
          await dispatch(cancelInvitation(invitation.id)).unwrap();
          addToast("success", translate("partnerships:cancel.successMessage"));
          setSelectedInvitation(null);
          refreshAll();
        } catch (err: any) {
          addToast("error", err?.message || translate("partnerships:cancel.errorMessage"));
        }
      },
    });
  };

  const handleResend = async (invitation: PartnerInvitation) => {
    try {
      await dispatch(resendInvitation(invitation.id)).unwrap();
      addToast("success", translate("partnerships:resend.successMessage"));
      refreshAll();
    } catch (err: any) {
      addToast("error", err?.message || translate("partnerships:resend.errorMessage"));
    }
  };

  // ─── Filtered Data ─────────────────────────────────────────

  const filteredPartnerships = useMemo(() => {
    let items = partnerships?.items || [];
    if (tierFilter !== null) {
      items = items.filter((p) => p.effectiveTier === tierFilter);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      items = items.filter((p) => {
        const nameA = (p.customerNameA || "").toLowerCase();
        const nameB = (p.customerNameB || "").toLowerCase();
        return nameA.includes(q) || nameB.includes(q);
      });
    }
    return items;
  }, [partnerships, tierFilter, searchText]);

  const filteredSent = useMemo(() => {
    let items = invitationsSent?.items || [];
    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      items = items.filter(
        (i) =>
          (i.inviteeEmail || "").toLowerCase().includes(q) ||
          (i.inviteeCustomerName || "").toLowerCase().includes(q)
      );
    }
    return items;
  }, [invitationsSent, searchText]);

  const filteredReceived = useMemo(() => {
    let items = invitationsReceived?.items || [];
    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      items = items.filter(
        (i) =>
          (i.inviterCustomerName || "").toLowerCase().includes(q) ||
          (i.inviteeEmail || "").toLowerCase().includes(q)
      );
    }
    return items;
  }, [invitationsReceived, searchText]);

  // ─── Sorted Sent Invitations (local client-side sorting) ──
  const sortedFilteredSent = useMemo(() => {
    const items = [...filteredSent];
    items.sort((a, b) => {
      let cmp = 0;
      if (sentSortField === "status") {
        cmp = a.statusCode - b.statusCode;
      } else {
        cmp = new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
      }
      return sentSortDir === "asc" ? cmp : -cmp;
    });
    return items;
  }, [filteredSent, sentSortField, sentSortDir]);

  const handleSentSort = (field: "status" | "date") => {
    if (sentSortField === field) {
      setSentSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSentSortField(field);
      setSentSortDir(field === "date" ? "desc" : "asc");
    }
  };

  // ─── Helper: Get partner name from relationship ────────────

  const getPartnerName = (p: PartnerRelationship) => {
    const isCurrentA =
      ownCustomer?.id?.toLowerCase() === p.customerIdA?.toLowerCase();
    return isCurrentA ? p.customerNameB : p.customerNameA;
  };

  const getPartnerLogo = (p: PartnerRelationship) => {
    const isCurrentA =
      ownCustomer?.id?.toLowerCase() === p.customerIdA?.toLowerCase();
    return isCurrentA ? p.customerLogoUrlB : p.customerLogoUrlA;
  };

  const getPartnerId = (p: PartnerRelationship) => {
    const isCurrentA =
      ownCustomer?.id?.toLowerCase() === p.customerIdA?.toLowerCase();
    return isCurrentA ? p.customerIdB : p.customerIdA;
  };

  const getTierLabel = (tier: RelationshipTierCode) => {
    switch (tier) {
      case RelationshipTierCode.Connected:
        return translate("partnerships:tier.connected");
      case RelationshipTierCode.Preferred:
        return translate("partnerships:tier.preferred");
      case RelationshipTierCode.Contracted:
        return translate("partnerships:tier.contracted");
      default:
        return translate("partnerships:tier.connected");
    }
  };

  const getStatusLabel = (status: InvitationStatusCode) => {
    switch (status) {
      case InvitationStatusCode.Pending:
        return translate("partnerships:invitation.pending");
      case InvitationStatusCode.Accepted:
        return translate("partnerships:invitation.accepted");
      case InvitationStatusCode.Declined:
        return translate("partnerships:invitation.declined");
      case InvitationStatusCode.Expired:
        return translate("partnerships:invitation.expired");
      case InvitationStatusCode.Cancelled:
        return translate("partnerships:invitation.cancelled");
      case InvitationStatusCode.Superseded:
        return translate("partnerships:invitation.superseded");
      default:
        return "";
    }
  };

  // ─── Toggle button counts ─────────────────────────────────

  const partnershipsCount = stats?.totalPartnerships || 0;
  const sentCount = invitationsSent?.items?.length || 0;
  const receivedPendingCount = invitationsReceived
    ? invitationsReceived.items.filter((i) => i.statusCode === InvitationStatusCode.Pending).length
    : 0;
  const receivedCount = invitationsReceived?.items?.length || 0;

  // ─── Shared Filter Content ─────────────────────────────────

  const renderSearchInput = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        background: dt.inputBg,
        borderRadius: dt.radiusSm,
        border: `1.5px solid ${searchFocused ? dt.blue : "transparent"}`,
        padding: "0 12px",
        height: "40px",
        boxShadow: searchFocused ? `0 0 0 3px ${dt.blueGlow}` : "none",
        transition: "all 0.2s ease",
        mb: "16px",
      }}
    >
      <SearchOutlined
        sx={{ fontSize: "16px", color: searchFocused ? dt.blue : dt.textMuted, flexShrink: 0, transition: "color 0.2s" }}
      />
      <input
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        placeholder={
          activeView === "partnerships"
            ? translate("partnerships:filter.search")
            : translate("partnerships:filter.searchInvitations")
        }
        style={{
          border: "none",
          outline: "none",
          width: "100%",
          fontSize: "13px",
          fontFamily: 'var(--font-sans)',
          color: dt.textPrimary,
          background: "transparent",
          marginLeft: "8px",
        }}
      />
    </Box>
  );

  const renderTierFilterPills = () => (
    <Box>
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1.5px",
          color: dt.textMuted,
          fontFamily: dt.font,
          mb: "10px",
          px: "4px",
        }}
      >
        {translate("partnerships:filter.tierFilter")}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {/* All */}
        <RedesignFilterItem
          label={translate("partnerships:tier.all")}
          count={partnerships?.items?.length || 0}
          isSelected={tierFilter === null}
          onClick={() => setTierFilter(null)}
        />
        {/* Connected */}
        <RedesignFilterItem
          label={translate("partnerships:tier.connected")}
          count={stats?.connectedCount || 0}
          isSelected={tierFilter === RelationshipTierCode.Connected}
          statusColor={tierStyleMap[RelationshipTierCode.Connected].text}
          indent
          onClick={() =>
            setTierFilter(
              tierFilter === RelationshipTierCode.Connected ? null : RelationshipTierCode.Connected
            )
          }
        />
        {/* Preferred */}
        <RedesignFilterItem
          label={translate("partnerships:tier.preferred")}
          count={stats?.preferredCount || 0}
          isSelected={tierFilter === RelationshipTierCode.Preferred}
          statusColor={tierStyleMap[RelationshipTierCode.Preferred].text}
          indent
          onClick={() =>
            setTierFilter(
              tierFilter === RelationshipTierCode.Preferred ? null : RelationshipTierCode.Preferred
            )
          }
        />
        {/* Contracted */}
        <RedesignFilterItem
          label={translate("partnerships:tier.contracted")}
          count={stats?.contractedCount || 0}
          isSelected={tierFilter === RelationshipTierCode.Contracted}
          statusColor={tierStyleMap[RelationshipTierCode.Contracted].text}
          indent
          onClick={() =>
            setTierFilter(
              tierFilter === RelationshipTierCode.Contracted
                ? null
                : RelationshipTierCode.Contracted
            )
          }
        />
      </Box>
    </Box>
  );

  const renderFilterContent = () => (
    <>
      {renderSearchInput()}
      {activeView === "partnerships" && renderTierFilterPills()}
    </>
  );

  // ─── Toggle Buttons ────────────────────────────────────────

  const renderToggleButtons = () => {
    const toggles: { key: ActiveView; label: string; count: number; hasPendingHighlight?: boolean }[] = [
      {
        key: "partnerships",
        label: translate("partnerships:page.partnershipsTab"),
        count: partnershipsCount,
      },
      {
        key: "sentInvitations",
        label: translate("partnerships:page.sentInvitationsTab"),
        count: sentCount,
      },
      {
        key: "receivedInvitations",
        label: translate("partnerships:page.receivedInvitationsTab"),
        count: receivedCount,
        hasPendingHighlight: receivedPendingCount > 0,
      },
    ];

    return (
      <Box
        sx={{
          display: "flex",
          gap: "6px",
          flexWrap: "wrap",
          p: isPhone ? "2px" : "3px",
          backgroundColor: dt.inputBg,
          borderRadius: dt.radiusPill,
          border: `1px solid ${dt.cardBorder}`,
          width: "fit-content",
        }}
      >
        {toggles.map((t) => {
          const isActive = activeView === t.key;
          return (
            <Box
              key={t.key}
              onClick={() => handleViewChange(t.key)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: isPhone ? "6px 12px" : "8px 16px",
                borderRadius: dt.radiusPill,
                cursor: "pointer",
                fontFamily: dt.font,
                fontSize: isPhone ? "12px" : "13px",
                fontWeight: 600,
                letterSpacing: "0.1px",
                whiteSpace: "nowrap",
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                ...(isActive
                  ? {
                      background: `linear-gradient(135deg, ${dt.blue}, ${dt.blueHover})`,
                      color: "#FFFFFF",
                      boxShadow: `0 2px 8px rgba(52, 152, 219, 0.3)`,
                    }
                  : {
                      background: "transparent",
                      color: dt.textSecondary,
                      "&:hover": {
                        backgroundColor: dt.blueGlow,
                        color: dt.blue,
                      },
                    }),
              }}
            >
              {t.label}
              <Box
                sx={{
                  fontSize: "11px",
                  fontFamily: dt.fontMono,
                  fontWeight: 700,
                  padding: "1px 7px",
                  borderRadius: "10px",
                  lineHeight: "16px",
                  minWidth: "20px",
                  textAlign: "center",
                  transition: "all 0.25s ease",
                  ...(isActive
                    ? {
                        backgroundColor: "rgba(255,255,255,0.25)",
                        color: "#FFFFFF",
                      }
                    : t.hasPendingHighlight
                    ? {
                        backgroundColor: dt.blue,
                        color: "#FFFFFF",
                      }
                    : {
                        backgroundColor: dt.cardBorder,
                        color: dt.textMuted,
                      }),
                }}
              >
                {t.count}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // ─── Partnerships Table ────────────────────────────────────

  const renderPartnershipsTable = () => {
    if (loading && !hasPartnershipsData) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: dt.blue }} />
        </Box>
      );
    }

    if (filteredPartnerships.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            borderRadius: dt.radiusSm,
            border: `1px dashed ${dt.cardBorder}`,
            backgroundColor: "#FAFBFC",
          }}
        >
          <HandshakeOutlinedIcon sx={{ fontSize: "48px", color: dt.textMuted, mb: 2 }} />
          <Box sx={{ fontSize: "16px", fontWeight: 600, color: dt.textPrimary, mb: 0.5 }}>
            {translate("partnerships:empty.noPartnerships")}
          </Box>
          <Box
            sx={{
              fontSize: "14px",
              color: dt.textMuted,
              textAlign: "center",
              maxWidth: "440px",
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            {translate("partnerships:empty.noPartnershipsDescription")}
          </Box>
          <InvitePartnerButton
            variant="contained"
            size="medium"
            label={translate("partnerships:empty.inviteFirst")}
            onSuccess={handleInviteSuccess}
          />
        </Box>
      );
    }

    if (isPhone) {
      return renderPartnershipsCards();
    }

    return (
      <Box>
        <Box sx={tableWrapperSx}>
          <Table sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "55%" }}>{translate("partnerships:table.partner")}</TableCell>
                <TableCell sx={{ width: "25%" }}>{translate("partnerships:table.tier")}</TableCell>
                <TableCell sx={{ width: "20%" }}>{translate("partnerships:table.since")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPartnerships.map((p) => {
                const partnerName = getPartnerName(p);
                const partnerLogo = getPartnerLogo(p);
                const tierStyle = tierStyleMap[p.effectiveTier] || tierStyleMap[RelationshipTierCode.Connected];
                const isHighlighted = changedPartnershipKeys.has(p.id);
                return (
                  <TableRow key={p.id} sx={isHighlighted ? highlightRowSx : undefined}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          src={partnerLogo || undefined}
                          sx={{
                            width: 34,
                            height: 34,
                            backgroundColor: tierStyle.bg,
                            flexShrink: 0,
                            border: `1.5px solid ${tierStyle.border}`,
                          }}
                        >
                          <BusinessOutlinedIcon sx={{ fontSize: "16px", color: tierStyle.text }} />
                        </Avatar>
                        <Tooltip title={partnerName || ""} enterDelay={400} arrow>
                          <Box
                            onClick={() => navigate(`/companies/${getPartnerId(p)}`)}
                            sx={{
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "320px",
                              color: dt.blue,
                              cursor: "pointer",
                              fontSize: "14px",
                              transition: "color 0.15s",
                              "&:hover": { textDecoration: "underline", color: dt.blueHover },
                            }}
                          >
                            {partnerName || translate("partnerships:partner.unknownCompany")}
                          </Box>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        {tierIconMap[p.effectiveTier]}
                        <RedesignStatusBadge
                          label={getTierLabel(p.effectiveTier)}
                          colorConfig={tierBadgeColors[p.effectiveTier] || tierBadgeColors[RelationshipTierCode.Connected]}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          fontFamily: dt.fontMono,
                          fontSize: "12px",
                          color: dt.textMuted,
                          letterSpacing: "0.3px",
                        }}
                      >
                        {dayjs(p.activatedAt).format("DD MMM YYYY")}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {/* Pagination sits flush against table bottom */}
          {partnerships && partnerships.items.length > 0 && (
            <Box
              sx={{
                borderTop: `1px solid ${dt.rowBorder}`,
                backgroundColor: dt.headerBg,
                padding: "6px 18px",
                ...paginationWrapperSx,
              }}
            >
              <LightTablePagination
                totalCount={partnerships.totalCount}
                currentPage={partnershipsQuery.pageNo}
                pageSize={partnershipsQuery.pageSize}
                pageSizeOptions={[10, 25, 50]}
                onPageChange={(p) =>
                  dispatch(getPartnerships({ ...partnershipsQuery, pageNo: p }))
                }
                onPageSizeChange={(s) =>
                  dispatch(getPartnerships({ ...partnershipsQuery, pageNo: 1, pageSize: s }))
                }
                rowsPerPageLabel={translate("partnerships:table.rowsPerPage")}
                ofLabel={translate("partnerships:table.of")}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderPartnershipsCards = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", overflow: "hidden", width: "100%" }}>
      {filteredPartnerships.map((p, index) => {
        const partnerName = getPartnerName(p);
        const partnerLogo = getPartnerLogo(p);
        const tierStyle = tierStyleMap[p.effectiveTier] || tierStyleMap[RelationshipTierCode.Connected];
        const isHighlighted = changedPartnershipKeys.has(p.id);
        return (
          <Box
            key={p.id}
            sx={{
              backgroundColor: dt.cardBg,
              ...(isHighlighted ? highlightRowSx : {}),
              borderRadius: dt.radiusSm,
              border: `1px solid ${dt.cardBorder}`,
              borderLeft: `3px solid ${tierStyle.text}`,
              padding: "12px",
              overflow: "hidden",
              width: "100%",
              boxSizing: "border-box",
              boxShadow: dt.cardShadow,
              animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
              "&:hover": {
                boxShadow: dt.cardShadowHover,
                transform: "translateY(-2px)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
                <Avatar
                  src={partnerLogo || undefined}
                  sx={{ width: 32, height: 32, backgroundColor: tierStyle.bg, flexShrink: 0 }}
                >
                  <BusinessOutlinedIcon sx={{ fontSize: "16px", color: tierStyle.text }} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    onClick={() => navigate(`/companies/${getPartnerId(p)}`)}
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: dt.blue,
                      cursor: "pointer",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {partnerName || translate("partnerships:partner.unknownCompany")}
                  </Box>
                  <Box sx={{ fontSize: "11px", fontFamily: dt.fontMono, color: dt.textMuted, mt: 0.25 }}>
                    {translate("partnerships:partner.since")}{" "}
                    {dayjs(p.activatedAt).format("DD MMM YYYY")}
                  </Box>
                </Box>
              </Box>
              <RedesignStatusBadge
                label={getTierLabel(p.effectiveTier)}
                colorConfig={tierBadgeColors[p.effectiveTier] || tierBadgeColors[RelationshipTierCode.Connected]}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  // ─── Invitation Company/Email Cell ──────────────────────────
  // Redesigned: contact card style with avatar, proper hierarchy

  const renderInvitationContactCell = (
    invitation: PartnerInvitation,
    type: "sent" | "received"
  ) => {
    const companyName =
      type === "received"
        ? invitation.inviterCustomerName
        : invitation.inviteeCustomerName;
    const logoUrl =
      type === "received"
        ? invitation.inviterCustomerLogoUrl
        : invitation.inviteeCustomerLogoUrl;
    const email = invitation.inviteeEmail;
    const isOnPlatform =
      type === "received"
        ? !!invitation.inviterCustomerId
        : !!invitation.inviteeCustomerId;

    if (isOnPlatform) {
      // Platform company: logo or initial avatar, company name primary, email secondary
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Avatar
            src={logoUrl || undefined}
            sx={{
              width: 34,
              height: 34,
              backgroundColor: "#EBF5FB",
              color: dt.blue,
              fontSize: "14px",
              fontWeight: 700,
              fontFamily: dt.font,
              flexShrink: 0,
              border: "1.5px solid #AED6F1",
            }}
          >
            {companyName ? companyName.charAt(0).toUpperCase() : <BusinessOutlinedIcon sx={{ fontSize: "16px" }} />}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={companyName || email} enterDelay={400} arrow>
              <Box
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: dt.textPrimary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "220px",
                }}
              >
                {companyName || email}
              </Box>
            </Tooltip>
            {companyName && (
              <Tooltip title={email} enterDelay={600} arrow>
                <Box
                  sx={{
                    fontSize: "11px",
                    color: dt.textMuted,
                    mt: "2px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "220px",
                    fontFamily: dt.fontMono,
                    letterSpacing: "0.2px",
                  }}
                >
                  {email}
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      );
    }

    // External user (not on platform): person icon, email primary, "External" chip
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Avatar
          sx={{
            width: 34,
            height: 34,
            backgroundColor: "#F4F6F9",
            color: dt.textMuted,
            flexShrink: 0,
            border: "1.5px solid #D1D5DB",
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: "18px" }} />
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Tooltip title={email} enterDelay={400} arrow>
            <Box
              sx={{
                fontWeight: 500,
                fontSize: "13px",
                color: dt.textPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "220px",
                fontFamily: dt.fontMono,
                letterSpacing: "0.2px",
              }}
            >
              {email}
            </Box>
          </Tooltip>
          <Chip
            label={translate("partnerships:invitation.external")}
            size="small"
            sx={{
              mt: "3px",
              height: "18px",
              fontSize: "10px",
              fontWeight: 600,
              fontFamily: dt.font,
              letterSpacing: "0.5px",
              backgroundColor: "#F4F6F9",
              color: dt.textMuted,
              border: "1px solid #E5E7EB",
              "& .MuiChip-label": { px: "6px" },
            }}
          />
        </Box>
      </Box>
    );
  };

  // ─── Invitations Table ─────────────────────────────────────

  const renderInvitationRow = (
    invitation: PartnerInvitation,
    type: "sent" | "received"
  ) => {
    const isPending = invitation.statusCode === InvitationStatusCode.Pending;
    const isExpired = invitation.statusCode === InvitationStatusCode.Expired;
    const isDeclined = invitation.statusCode === InvitationStatusCode.Declined;
    const isCancelled = invitation.statusCode === InvitationStatusCode.Cancelled;
    const changedKeys = type === "sent" ? changedSentKeys : changedReceivedKeys;
    const isHighlighted = changedKeys.has(invitation.id);

    return (
      <TableRow
        key={invitation.id}
        sx={{
          backgroundColor:
            type === "received" && isPending ? "rgba(52, 152, 219, 0.03)" : undefined,
          ...(isHighlighted ? highlightRowSx : {}),
        }}
      >
        <TableCell sx={{ width: "30%" }}>
          {renderInvitationContactCell(invitation, type)}
        </TableCell>
        <TableCell sx={{ width: "14%" }}>
          <RedesignStatusBadge
            label={getStatusLabel(invitation.statusCode)}
            colorConfig={invitationBadgeColors[invitation.statusCode] || invitationBadgeColors[InvitationStatusCode.Pending]}
          />
        </TableCell>
        <TableCell sx={{ width: "20%" }}>
          {invitation.message ? (
            <Tooltip title={invitation.message} enterDelay={400} arrow>
              <Box
                sx={{
                  fontSize: "13px",
                  color: dt.textSecondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "180px",
                  fontStyle: "italic",
                }}
              >
                "{invitation.message}"
              </Box>
            </Tooltip>
          ) : (
            <Box sx={{ fontSize: "12px", color: dt.textMuted, fontStyle: "italic" }}>
              {translate("partnerships:invitation.noMessage")}
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ width: "12%" }}>
          <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", color: dt.textMuted, letterSpacing: "0.3px", whiteSpace: "nowrap" }}>
            {dayjs(invitation.createdDate).format("DD MMM YYYY")}
          </Box>
        </TableCell>
        {!isCompact && (
          <TableCell sx={{ width: "12%" }}>
            <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", color: dt.textMuted, whiteSpace: "nowrap" }}>
              {isPending && invitation.expiresAt
                ? dayjs(invitation.expiresAt).format("DD MMM YYYY")
                : "\u2014"}
            </Box>
          </TableCell>
        )}
        <TableCell sx={{ width: "12%" }}>
          <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
            {type === "received" && isPending && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "15px !important" }} />}
                  disabled={actionLoading}
                  onClick={() => handleAcceptClick(invitation)}
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: dt.radiusSm,
                    padding: "4px 12px",
                    backgroundColor: "#27AE60",
                    color: "#FFFFFF",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#219A52",
                      boxShadow: "0 2px 8px rgba(39,174,96,0.3)",
                    },
                  }}
                >
                  {translate("partnerships:actions.accept")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CancelOutlinedIcon sx={{ fontSize: "15px !important" }} />}
                  disabled={actionLoading}
                  onClick={() => handleDeclineClick(invitation)}
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: dt.radiusSm,
                    padding: "4px 12px",
                    color: "#C0392B",
                    borderColor: "#F5B7B1",
                    "&:hover": {
                      borderColor: "#C0392B",
                      backgroundColor: "rgba(192,57,43,0.04)",
                    },
                  }}
                >
                  {translate("partnerships:actions.decline")}
                </Button>
              </>
            )}
            {type === "sent" && isPending && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<BlockOutlinedIcon sx={{ fontSize: "15px !important" }} />}
                disabled={actionLoading}
                onClick={() => handleCancelClick(invitation)}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: dt.radiusSm,
                  padding: "4px 12px",
                  color: dt.textSecondary,
                  borderColor: dt.cardBorder,
                  "&:hover": {
                    borderColor: dt.blue,
                    color: dt.blue,
                    backgroundColor: dt.blueSoft,
                  },
                }}
              >
                {translate("partnerships:actions.cancel")}
              </Button>
            )}
            {type === "sent" && (isExpired || isDeclined || isCancelled) && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ReplayOutlinedIcon sx={{ fontSize: "15px !important" }} />}
                disabled={actionLoading}
                onClick={() => handleResend(invitation)}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: dt.radiusSm,
                  padding: "4px 12px",
                  color: dt.blue,
                  borderColor: dt.blue,
                  "&:hover": { backgroundColor: dt.blueSoft },
                }}
              >
                {translate("partnerships:actions.resend")}
              </Button>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderInvitationCard = (
    invitation: PartnerInvitation,
    type: "sent" | "received",
    index: number
  ) => {
    const statusStyle =
      invitationStatusStyleMap[invitation.statusCode] ||
      invitationStatusStyleMap[InvitationStatusCode.Pending];
    const isPending = invitation.statusCode === InvitationStatusCode.Pending;
    const isExpired = invitation.statusCode === InvitationStatusCode.Expired;
    const isDeclined = invitation.statusCode === InvitationStatusCode.Declined;
    const isCancelled = invitation.statusCode === InvitationStatusCode.Cancelled;
    const changedKeys = type === "sent" ? changedSentKeys : changedReceivedKeys;
    const isHighlighted = changedKeys.has(invitation.id);

    const companyName =
      type === "received"
        ? invitation.inviterCustomerName
        : invitation.inviteeCustomerName;
    const logoUrl =
      type === "received"
        ? invitation.inviterCustomerLogoUrl
        : invitation.inviteeCustomerLogoUrl;
    const isOnPlatform =
      type === "received"
        ? !!invitation.inviterCustomerId
        : !!invitation.inviteeCustomerId;
    const displayName = companyName || invitation.inviteeEmail;

    return (
      <Box
        key={invitation.id}
        sx={{
          backgroundColor:
            type === "received" && isPending
              ? "rgba(52, 152, 219, 0.03)"
              : dt.cardBg,
          ...(isHighlighted ? highlightRowSx : {}),
          borderRadius: dt.radiusSm,
          border: `1px solid ${dt.cardBorder}`,
          borderLeft: `3px solid ${statusStyle.text}`,
          padding: "12px",
          overflow: "hidden",
          width: "100%",
          boxSizing: "border-box",
          boxShadow: dt.cardShadow,
          animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
          "&:hover": {
            boxShadow: dt.cardShadowHover,
            transform: "translateY(-2px)",
          },
        }}
      >
        {/* Top: company + status */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
            <Avatar
              src={isOnPlatform ? (logoUrl || undefined) : undefined}
              sx={{
                width: 30,
                height: 30,
                flexShrink: 0,
                fontSize: "13px",
                fontWeight: 700,
                fontFamily: dt.font,
                ...(isOnPlatform
                  ? { backgroundColor: "#EBF5FB", color: dt.blue, border: "1.5px solid #AED6F1" }
                  : { backgroundColor: "#F4F6F9", color: dt.textMuted, border: "1.5px solid #D1D5DB" }),
              }}
            >
              {isOnPlatform
                ? (companyName ? companyName.charAt(0).toUpperCase() : <BusinessOutlinedIcon sx={{ fontSize: "14px" }} />)
                : <PersonOutlineIcon sx={{ fontSize: "16px" }} />}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: dt.textPrimary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayName}
              </Box>
              {companyName && (
                <Box sx={{ fontSize: "11px", color: dt.textMuted, mt: 0.25, fontFamily: dt.fontMono }}>
                  {invitation.inviteeEmail}
                </Box>
              )}
              {!isOnPlatform && (
                <Chip
                  label={translate("partnerships:invitation.external")}
                  size="small"
                  sx={{
                    mt: "2px",
                    height: "16px",
                    fontSize: "9px",
                    fontWeight: 600,
                    fontFamily: dt.font,
                    backgroundColor: "#F4F6F9",
                    color: dt.textMuted,
                    border: "1px solid #E5E7EB",
                    "& .MuiChip-label": { px: "5px" },
                  }}
                />
              )}
            </Box>
          </Box>
          <RedesignStatusBadge
            label={getStatusLabel(invitation.statusCode)}
            colorConfig={invitationBadgeColors[invitation.statusCode] || invitationBadgeColors[InvitationStatusCode.Pending]}
          />
        </Box>

        {/* Message preview */}
        {invitation.message && (
          <Box
            sx={{
              fontSize: "12px",
              color: dt.textSecondary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mb: 1,
              fontStyle: "italic",
            }}
          >
            "{invitation.message}"
          </Box>
        )}

        {/* Bottom: date + actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ fontSize: "11px", fontFamily: dt.fontMono, color: dt.textMuted }}>
            {translate("partnerships:invitation.sentOn")}{" "}
            {dayjs(invitation.createdDate).format("DD MMM YYYY")}
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
            {type === "received" && isPending && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  disabled={actionLoading}
                  onClick={() => handleAcceptClick(invitation)}
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "11px",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: dt.radiusSm,
                    padding: "3px 10px",
                    backgroundColor: "#27AE60",
                    color: "#FFF",
                    boxShadow: "none",
                    minWidth: "auto",
                    "&:hover": { backgroundColor: "#219A52" },
                  }}
                >
                  {translate("partnerships:actions.accept")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={actionLoading}
                  onClick={() => handleDeclineClick(invitation)}
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "11px",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: dt.radiusSm,
                    padding: "3px 10px",
                    color: "#C0392B",
                    borderColor: "#F5B7B1",
                    minWidth: "auto",
                    "&:hover": { borderColor: "#C0392B", backgroundColor: "rgba(192,57,43,0.04)" },
                  }}
                >
                  {translate("partnerships:actions.decline")}
                </Button>
              </>
            )}
            {type === "sent" && isPending && (
              <Button
                size="small"
                variant="outlined"
                disabled={actionLoading}
                onClick={() => handleCancelClick(invitation)}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: dt.radiusSm,
                  padding: "3px 10px",
                  color: dt.textSecondary,
                  borderColor: dt.cardBorder,
                  minWidth: "auto",
                  "&:hover": { borderColor: dt.blue, color: dt.blue, backgroundColor: dt.blueSoft },
                }}
              >
                {translate("partnerships:actions.cancel")}
              </Button>
            )}
            {type === "sent" && (isExpired || isDeclined || isCancelled) && (
              <Button
                size="small"
                variant="outlined"
                disabled={actionLoading}
                onClick={() => handleResend(invitation)}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: dt.radiusSm,
                  padding: "3px 10px",
                  color: dt.blue,
                  borderColor: dt.blue,
                  minWidth: "auto",
                  "&:hover": { backgroundColor: dt.blueSoft },
                }}
              >
                {translate("partnerships:actions.resend")}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderInvitationsContent = (
    items: PartnerInvitation[],
    type: "sent" | "received",
    emptyKey: string
  ) => {
    const hasData = type === "sent" ? hasSentData : hasReceivedData;
    if (loading && !hasData) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: dt.blue }} />
        </Box>
      );
    }

    if (items.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
            borderRadius: dt.radiusSm,
            border: `1px dashed ${dt.cardBorder}`,
            backgroundColor: "#FAFBFC",
          }}
        >
          <MailOutlineIcon sx={{ fontSize: "36px", color: dt.textMuted, mb: 1.5 }} />
          <Box sx={{ fontSize: "14px", color: dt.textMuted, textAlign: "center" }}>
            {translate(emptyKey)}
          </Box>
        </Box>
      );
    }

    if (isPhone) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            overflow: "hidden",
            width: "100%",
          }}
        >
          {items.map((inv, idx) => renderInvitationCard(inv, type, idx))}
        </Box>
      );
    }

    // Use sorted items for "sent" tab, original items for "received"
    const displayItems = type === "sent" ? sortedFilteredSent : items;

    return (
      <Box sx={tableWrapperSx}>
        <Table sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "30%" }}>{translate("partnerships:invitation.company")}</TableCell>
              <TableCell sx={{ width: "14%" }}>
                {type === "sent" ? (
                  <TableSortLabel
                    active={sentSortField === "status"}
                    direction={sentSortField === "status" ? sentSortDir : "asc"}
                    onClick={() => handleSentSort("status")}
                    sx={{
                      "&.MuiTableSortLabel-root": {
                        color: "inherit !important",
                      },
                    }}
                  >
                    {translate("partnerships:invitation.status")}
                  </TableSortLabel>
                ) : (
                  translate("partnerships:invitation.status")
                )}
              </TableCell>
              <TableCell sx={{ width: "20%" }}>{translate("partnerships:invitation.message")}</TableCell>
              <TableCell sx={{ width: "12%" }}>
                {type === "sent" ? (
                  <TableSortLabel
                    active={sentSortField === "date"}
                    direction={sentSortField === "date" ? sentSortDir : "desc"}
                    onClick={() => handleSentSort("date")}
                    sx={{
                      "&.MuiTableSortLabel-root": {
                        color: "inherit !important",
                      },
                    }}
                  >
                    {translate("partnerships:invitation.dateSent")}
                  </TableSortLabel>
                ) : (
                  translate("partnerships:invitation.dateSent")
                )}
              </TableCell>
              {!isCompact && (
                <TableCell sx={{ width: "12%" }}>{translate("partnerships:invitation.expiry")}</TableCell>
              )}
              <TableCell sx={{ width: "12%" }}>{translate("partnerships:invitation.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayItems.map((inv) => renderInvitationRow(inv, type))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  // ─── Active view content ───────────────────────────────────

  const renderActiveContent = () => {
    switch (activeView) {
      case "partnerships":
        return renderPartnershipsTable();
      case "sentInvitations":
        return renderInvitationsContent(filteredSent, "sent", "partnerships:empty.noSentInvitations");
      case "receivedInvitations":
        return renderInvitationsContent(filteredReceived, "received", "partnerships:empty.noReceivedInvitations");
      default:
        return null;
    }
  };

  // =================================================================
  // RENDER
  // =================================================================

  return (
    <Box
      sx={{
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        flex: 1,
        overflowX: "hidden",
        px: { xs: "16px", md: "32px" },
      }}
    >
      {/* ─── Page Header ───────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: { xs: 1.5, md: 2 },
          pt: { xs: 2, md: 3 },
          pb: 2,
          animation: "headerReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontSize: { xs: "20px", sm: "22px", md: "28px" },
              fontWeight: 800,
              color: dt.textPrimary,
              letterSpacing: "-0.5px",
            }}
          >
            {translate("partnerships:page.title")}
          </Box>
          <Box
            sx={{
              width: "48px",
              height: "3px",
              borderRadius: "2px",
              background: `linear-gradient(90deg, ${dt.blue}, ${dt.blue}00)`,
              mt: 1,
            }}
          />
        </Box>

        <InvitePartnerButton
          variant="contained"
          size={isPhone ? "small" : "medium"}
          onSuccess={handleInviteSuccess}
        />
      </Box>

      {/* ─── Main Content Area ─────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          pb: 3,
          flex: 1,
          alignItems: "flex-start",
          minWidth: 0,
          overflow: "hidden",
          animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
        }}
      >
        {/* ─── Desktop Sidebar (>1200px) ────────────────── */}
        {!isCompact && (
          <Box
            sx={{
              width: "260px",
              minWidth: "260px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
            }}
          >
            <Box
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusLg,
                border: `1px solid ${dt.cardBorder}`,
                padding: "22px",
                boxShadow: dt.cardShadow,
              }}
            >
              {renderFilterContent()}
            </Box>
          </Box>
        )}

        {/* ─── Main Area (toggle + table) ───────────────── */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            gap: "16px",
          }}
        >
          {/* Accordion filters (tablet & phone) */}
          {isCompact && (
            <Accordion
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: `${dt.radiusSm} !important`,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: "none !important",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 !important" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: "44px !important",
                  "& .MuiAccordionSummary-content": { margin: "8px 0 !important" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: dt.textSecondary,
                  }}
                >
                  <FilterListOutlinedIcon sx={{ fontSize: "16px" }} />
                  {translate("partnerships:filter.filters")}
                  {(tierFilter !== null || searchText) && (
                    <Box
                      sx={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: dt.blue,
                      }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>{renderFilterContent()}</AccordionDetails>
            </Accordion>
          )}

          {/* Toggle Buttons */}
          {renderToggleButtons()}

          {/* Table / Cards Content — no wrapper card, tables have their own styling */}
          {renderActiveContent()}
        </Box>
      </Box>

      {/* ─── Accept Dialog ────────────────────────────────── */}
      <AcceptInvitationDialog
        open={acceptDialogOpen}
        invitation={selectedInvitation}
        onConfirm={handleAcceptConfirm}
        onClose={() => {
          setAcceptDialogOpen(false);
          setSelectedInvitation(null);
        }}
      />

      {/* ─── Toast Stack ─────────────────────────────────── */}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </Box>
  );
};

export default PartnershipsPage;
